import { useState } from "react";
import { Box,Typography } from "@mui/material";
import { Routes,Route,Navigate,useNavigate } from "react-router-dom";
import Sidebar from "../../login/Admin/AdminDashboard/Sidebar";
import TopBar from "../../login/Admin/AdminDashboard/TopBar";
import AdminAdministrators from "./AdminAdministrators";
import AdminTeamDelegates from "./AdminTeamDelegates";
import AdminCategories from "./AdminCategories";
import AdminTeams from "./AdminTeams";
import AdminTeam from "./AdminTeams/index";
import AdminTransferMarket from "./AdminTransferMarket";
import AdminVenues from "./AdminVenues";
import AdminReferees from "./AdminReferees";
import AdminTournaments from "./AdminTournaments";
import TournamentConfiguration from "./AdminTournaments/TournamentConfiguration";
import TournamentPhases from "./AdminTournaments/TournamentPhases";
import AdminMatches from "./AdminMatches";
import LiveMatchControl from "../../components/Admin/Matches/LiveMatchControl";
import GalleryManager from "../../components/Admin/Gallery/GalleryManager";
import futbolito from "../../static/futbolito.png";
import { useAppTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";

const expandedWidth=260;
const collapsedWidth=76;

function DashboardHome(){
  const navigate=useNavigate();
  const {colors,typography}=useAppTheme();
  const {user}=useAuth();

  const nombre=user?.nombre_completo||user?.first_name||"Administrador";

  const cardSx={
    width:"100%",
    maxWidth:360,
    p:2,
    minHeight:125,
    border:`1px solid ${colors.border}`,
    borderRadius:"12px",
    backgroundColor:colors.surface,
    cursor:"pointer",
    transition:"border-color .2s ease, background-color .2s ease, transform .2s ease",
    "&:hover":{
      borderColor:colors.accent,
      backgroundColor:colors.backgroundSoft,
      transform:"translateY(-2px)",
    },
  };

  const quickAccess=[
    {
      title:"Equipos",
      description:"Registra equipos y administra sus jugadores por categoría.",
      path:"/admin/teams",
    },
    {
      title:"Torneos",
      description:"Configura torneos, categorías, participantes y fases.",
      path:"/admin/tournaments",
    },
    {
      title:"Calendario de partidos",
      description:"Consulta y administra los partidos programados.",
      path:"/admin/matches",
    },
    {
      title:"Delegados de equipos",
      description:"Registra y administra las cuentas de los delegados.",
      path:"/admin/team-delegates",
    },
    {
      title:"Canchas",
      description:"Administra las canchas disponibles para los partidos.",
      path:"/admin/venues",
    },
    {
      title:"Árbitros",
      description:"Registra y administra los árbitros disponibles.",
      path:"/admin/referees",
    },
  ];

  return (
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box
        sx={{
          position:"relative",
          minHeight:{xs:185,sm:175},
          mb:3,
          px:{xs:2,sm:3},
          py:{xs:2,sm:2.5},
          display:"flex",
          alignItems:"center",
          overflow:"hidden",
          border:`1px solid ${colors.border}`,
          borderRadius:"14px",
          backgroundColor:colors.surface,
        }}
      >
        <Box
          sx={{
            width:{xs:105,sm:145},
            minWidth:{xs:105,sm:145},
            alignSelf:"stretch",
            display:"flex",
            alignItems:"flex-end",
            justifyContent:"center",
            mr:{xs:1.5,sm:3},
          }}
        >
          <Box
            component="img"
            src={futbolito}
            alt="Futbolito"
            sx={{
              display:"block",
              width:{xs:110,sm:150},
              maxHeight:{xs:155,sm:170},
              objectFit:"contain",
              objectPosition:"center bottom",
            }}
          />
        </Box>

        <Box sx={{position:"relative",zIndex:1,minWidth:0}}>
          <Typography
            sx={{
              mb:0.5,
              fontSize:typography.sizes.md,
              fontWeight:600,
              color:colors.accent,
            }}
          >
            PANEL ADMINISTRATIVO
          </Typography>

          <Typography
            sx={{
              fontSize:{xs:typography.sizes.xl,sm:typography.sizes.pageTitle||typography.sizes.title},
              fontWeight:700,
              lineHeight:1.2,
              color:colors.text,
            }}
          >
            Bienvenido, {nombre}
          </Typography>

          <Typography
            sx={{
              mt:0.8,
              maxWidth:650,
              fontSize:typography.sizes.md,
              lineHeight:1.55,
              color:colors.textSecondary,
            }}
          >
            Administra la liga, organiza tus torneos y mantén el control de equipos, partidos y competiciones desde un solo lugar.
          </Typography>
        </Box>
      </Box>

      <Typography sx={{mb:1.5,fontSize:typography.sizes.xl,fontWeight:700,color:colors.text}}>
        Accesos rápidos
      </Typography>

      <Box
        sx={{
          display:"grid",
          gridTemplateColumns:{xs:"1fr",sm:"repeat(2,minmax(0,360px))",lg:"repeat(3,minmax(0,360px))"},
          gap:1.5,
          justifyContent:"start",
        }}
      >
        {quickAccess.map(item=>(
          <Box key={item.path} onClick={()=>navigate(item.path)} sx={cardSx}>
            <Typography sx={{fontSize:typography.sizes.lg,fontWeight:700,color:colors.text}}>
              {item.title}
            </Typography>

            <Typography sx={{mt:0.7,fontSize:typography.sizes.md,lineHeight:1.5,color:colors.textSecondary}}>
              {item.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function SuperAdminRoute({children}){
  const {user}=useAuth();

  if(user?.rol!=="SUPERADMIN"){
    return <Navigate to="/admin" replace />;
  }

  return children;
}

function AdminOrSuperAdminRoute({children}){
  const {user}=useAuth();

  if(!["SUPERADMIN","ADMIN"].includes(user?.rol)){
    return <Navigate to="/admin" replace />;
  }

  return children;
}

export default function AdminDashboard(){
  const {colors}=useAppTheme();

  const [sidebarExpanded,setSidebarExpanded]=useState(
    ()=>localStorage.getItem("sidebarExpanded")==="true"
  );

  const [mobileOpen,setMobileOpen]=useState(false);

  const handleToggleSidebar=()=>{
    setSidebarExpanded(prev=>{
      const next=!prev;
      localStorage.setItem("sidebarExpanded",String(next));
      return next;
    });
  };

  return (
    <Box sx={{display:"flex",minHeight:"100vh",backgroundColor:colors.background}}>
      <Sidebar
        expanded={sidebarExpanded}
        mobileOpen={mobileOpen}
        onMobileClose={()=>setMobileOpen(false)}
        expandedWidth={expandedWidth}
        collapsedWidth={collapsedWidth}
      />

      <Box
        component="main"
        sx={{
          flex:1,
          minWidth:0,
          minHeight:"100vh",
          backgroundColor:colors.background,
          transition:"background-color .2s ease"
        }}
      >
        <TopBar
          sidebarExpanded={sidebarExpanded}
          onToggleSidebar={handleToggleSidebar}
          onOpenMobile={()=>setMobileOpen(true)}
        />

        <Box
          sx={{
            p:{xs:1.5,sm:2,md:3},
            minHeight:"calc(100vh - 72px)",
            backgroundColor:colors.background,
            transition:"background-color .2s ease"
          }}
        >
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="administrators" element={<SuperAdminRoute><AdminAdministrators /></SuperAdminRoute>} />
            <Route path="categories" element={<AdminOrSuperAdminRoute><AdminCategories /></AdminOrSuperAdminRoute>} />
            <Route path="teams" element={<AdminOrSuperAdminRoute><AdminTeams /></AdminOrSuperAdminRoute>} />
            <Route path="teams/:teamSlug/*" element={<AdminOrSuperAdminRoute><AdminTeam /></AdminOrSuperAdminRoute>} />
            <Route path="team-delegates" element={<AdminOrSuperAdminRoute><AdminTeamDelegates /></AdminOrSuperAdminRoute>} />
            <Route path="transfer-market" element={<AdminOrSuperAdminRoute><AdminTransferMarket /></AdminOrSuperAdminRoute>} />
            <Route path="venues" element={<AdminOrSuperAdminRoute><AdminVenues /></AdminOrSuperAdminRoute>} />
            <Route path="referees" element={<AdminOrSuperAdminRoute><AdminReferees /></AdminOrSuperAdminRoute>} />
            <Route path="tournaments" element={<AdminOrSuperAdminRoute><AdminTournaments /></AdminOrSuperAdminRoute>} />
            <Route path="tournaments/:slug" element={<AdminOrSuperAdminRoute><TournamentConfiguration /></AdminOrSuperAdminRoute>} />
            <Route path="tournaments/:slug/:categorySlug/phases" element={<AdminOrSuperAdminRoute><TournamentPhases /></AdminOrSuperAdminRoute>} />
            <Route path="matches" element={<AdminOrSuperAdminRoute><AdminMatches /></AdminOrSuperAdminRoute>} />
            <Route path="matches/:tournamentSlug/:categorySlug" element={<AdminOrSuperAdminRoute><AdminMatches /></AdminOrSuperAdminRoute>} />
            <Route path="matches/live/:slug" element={<AdminOrSuperAdminRoute><LiveMatchControl /></AdminOrSuperAdminRoute>} />
            <Route path="gallery" element={<AdminOrSuperAdminRoute><GalleryManager /></AdminOrSuperAdminRoute>} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
}